import { ArrowForwardRounded } from "@mui/icons-material";
import {
  Box,
  Container,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { Theme, useTheme } from "@mui/material/styles";
import { useRef } from "react";
import { scrollToAnchor } from "../Utils/scroll";
import PageSectionBG from "./PageSectionBG";
import TiltCard from "./TiltCard";

interface ContactCTASectionProps {
  bgcolor?: "light" | "dark" | "pink";
  paddingBottom?: number;
  paddingTop?: number;
  canTilt?: boolean;
}

const ContactCTASection = ({
  bgcolor = "dark",
  canTilt = false,
  paddingBottom = 4,
  paddingTop = 4,
}: ContactCTASectionProps) => {
  const containerRef = useRef(null);
  // const navigate = useNavigate();
  const theme = useTheme();

  return (
    <PageSectionBG
      bgcolor={"transparent"}
      id={`contact-cta-${bgcolor}`}
      paddingBottom={paddingBottom}
      paddingTop={paddingTop}
    >
      <Box
        component="div"
        ref={containerRef}
        sx={{ position: "relative" }}
        paddingBottom={4}
        paddingTop={4}
      >
        <Container maxWidth="xl">
          <TiltCard canTilt={canTilt} containerRef={containerRef}>
            <Grid2
              container
              justifyContent="space-between"
              width={"100%"}
              spacing={0}
              sx={{
                borderRadius: 4,
                boxShadow: 8,
                background:
                  bgcolor === "light"
                    ? theme.palette.gray.main
                    : bgcolor === "pink"
                    ? theme.palette.pink.main
                    : theme.palette.gradients.wildGreenBoy,
              }}
            >
              <Grid2
                xs={12}
                sm={12}
                md={10}
                lg={8}
                pl={{ xs: 4, sm: 4, md: 10 }}
                pt={{ xs: 4, sm: 4, md: 7 }}
                pr={0}
              >
                <Stack direction="column" gap={3} pr={{ xs: 4, sm: 4, md: 0 }}>
                  <Stack direction="column" gap={1}>
                    <Typography
                      className="noselect"
                      fontWeight={"700"}
                      variant="h3"
                      color={
                        bgcolor === "light"
                          ? theme.palette.primary.main
                          : theme.palette.common.white
                      }
                    >
                      Let's get your project started
                    </Typography>

                    <Typography
                      variant="textXLLight"
                      fontWeight={"700"}
                      color={
                        bgcolor === "light"
                          ? theme.palette.primary.main
                          : theme.palette.common.white
                      }
                    >
                      Know what you want or have questions?
                    </Typography>
                  </Stack>

                  <Typography
                    variant="textXLMedium"
                    color={
                      bgcolor === "light"
                        ? theme.palette.primary.main
                        : theme.palette.common.white
                    }
                  >
                    Send us a message with some details about your project and
                    we will schedule some time to chat and get things moving!
                  </Typography>

                  <Button
                    variant="contained"
                    title="Build Something Amazing"
                    endIcon={
                      <ArrowForwardRounded
                        sx={{ fontSize: { xs: 24, sm: 28, md: 36 } }}
                      />
                    }
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.4rem" },
                      width: { xs: "100%", sm: "100%", md: "fit-content" },
                      background:
                        bgcolor === "light"
                          ? theme.palette.yellow.main
                          : bgcolor === "pink"
                          ? theme.palette.yellow.main
                          : theme.palette.primary.main,
                      color:
                        bgcolor === "light" || bgcolor === "pink"
                          ? theme.palette.primary.main
                          : "#9DFB34",
                      textTransform: "none",
                      fontWeight: 700,
                      py: 1.5,
                      mt: 1,
                      "&:hover": {
                        opacity: 0.975,
                        background:
                          bgcolor === "light"
                            ? `${theme.palette.yellow.main}CC`
                            : bgcolor === "pink"
                            ? `${theme.palette.yellow.main}CC`
                            : `${theme.palette.primary.main}CC`,
                      },
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToAnchor({ title: "Contact", link: "/#/contact" });
                    }}
                    disableElevation
                  >
                    Build Something Amazing
                  </Button>
                </Stack>
              </Grid2>
              <Grid2
                pt={{ xs: "2.3rem", sm: "2.3rem", md: 0 }}
                justifyContent="end"
                xs={12}
                sm={12}
                md={10}
                lg={4}
              >
                <img
                  width={
                    useMediaQuery((theme: Theme) => theme.breakpoints.up("md"))
                      ? "85%"
                      : "100%"
                  }
                  src={
                    useMediaQuery((theme: Theme) => theme.breakpoints.up("md"))
                      ? "./assets/elements/title_3d_element.png"
                      : "./assets/elements/title_3d_element_mobile.png"
                  }
                  alt="3D aesthetic element"
                  style={{ float: "right" }}
                  loading="lazy"
                  decoding="async"
                />
              </Grid2>
            </Grid2>
          </TiltCard>
        </Container>
      </Box>
    </PageSectionBG>
  );
};

export default ContactCTASection;
